import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'
import { ExclusionAreaGeoJSON, RefData } from '../../../../types/apiModels'
import { EXCLUSION_AREA_TYPE_IDS } from '../../../../utils/constants'
import { Switch } from '../../../Switch/Switch'
import { formattedHectaresArea } from '../../utils'

interface ExclusionAreaEditItemProps {
  exclusionArea: ExclusionAreaGeoJSON
  exclusionAreaTypes: RefData[] | null
  onSave: (updatedArea: ExclusionAreaGeoJSON) => void
  onCancel: () => void
  onChange?: (updatedArea: ExclusionAreaGeoJSON) => void
}

export const ExclusionAreaEditItem: React.FC<ExclusionAreaEditItemProps> = ({
  exclusionArea,
  exclusionAreaTypes,
  onSave,
  onCancel,
  onChange,
}) => {
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(exclusionArea)
  }

  const handleExclusionTypeChange = (exclusionAreaTypeId: number) => {
    const selectedType = exclusionAreaTypes?.find(type => type.id === exclusionAreaTypeId)
    const updated: ExclusionAreaGeoJSON = {
      ...exclusionArea,
      properties: {
        ...exclusionArea.properties,
        exclusionAreaTypeId: Number(exclusionAreaTypeId),
        name: selectedType?.es_AR || '',
      },
    }
    onChange?.(updated)
  }

  const handleNameChange = (otherName: string) => {
    const updated: ExclusionAreaGeoJSON = {
      ...exclusionArea,
      properties: {
        ...exclusionArea.properties,
        otherName,
      },
    }
    onChange?.(updated)
  }

  const handleGrazingChange = (hasGrazingManagement: boolean) => {
    const updated: ExclusionAreaGeoJSON = {
      ...exclusionArea,
      properties: {
        ...exclusionArea.properties,
        hasGrazingManagement,
      },
    }
    onChange?.(updated)
  }

  return (
    <form style={{ width: '100%' }} onSubmit={handleSave}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
          alignItems: 'center',
          p: 2,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ minWidth: 130, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth required size="small">
            <InputLabel id="exclusion-category-edit-label">Categoría</InputLabel>
            <Select
              error={exclusionArea.properties.exclusionAreaTypeId === null}
              label="Categoría"
              labelId="exclusion-category-edit-label"
              title={exclusionArea.properties.name || ''}
              value={exclusionArea.properties.exclusionAreaTypeId ?? ''}
              onChange={e => handleExclusionTypeChange(Number(e.target.value))}
            >
              {exclusionAreaTypes?.map(type => (
                <MenuItem
                  key={type.id}
                  disabled={String(type.id) === EXCLUSION_AREA_TYPE_IDS.OTHER}
                  value={type.id}
                >
                  {type.es_AR}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            required
            label="Nombre"
            size="small"
            title={exclusionArea.properties.otherName || ''}
            value={exclusionArea.properties.otherName || ''}
            onChange={e => handleNameChange(e.target.value)}
          />
        </Box>

        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row', gap: 1 }}>
          <Switch
            checked={exclusionArea.properties.hasGrazingManagement || false}
            onChange={e => handleGrazingChange(e.target.checked)}
          />
          <Typography variant="body2">Con pastoreo</Typography>
        </Box>

        <Stack spacing={4} sx={{ alignItems: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            {formattedHectaresArea(exclusionArea)}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <IconButton aria-label="save" size="small" type="submit">
              <CheckIcon />
            </IconButton>

            <IconButton aria-label="cancel" size="small" onClick={onCancel}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </form>
  )
}

export default ExclusionAreaEditItem
